import { apiFetch } from '@/hooks/useFetch';

/**
 * API client for fetching phrases and categories from the backend.
 * Uses centralized apiFetch for consistent error handling and caching.
 */

/**
 * Fetch all categories from the API
 */
export const fetchCategories = async () => {
  const { data, error } = await apiFetch('/api/phrases/categories');
  if (error) {
    throw new Error(error || 'Failed to fetch categories');
  }
  return data;
};

/**
 * Fetch all phrases with optional filtering
 */
export const fetchPhrases = async ({ category = null, search = null, limit = 5000, skip = 0 } = {}) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  if (limit) params.append('limit', limit.toString());
  if (skip) params.append('skip', skip.toString());
  
  const { data, error } = await apiFetch(`/api/phrases?${params.toString()}`);
  if (error) {
    throw new Error(error || 'Failed to fetch phrases');
  }
  return data;
};

/**
 * Fetch a single phrase by ID
 */
export const fetchPhraseById = async (id) => {
  const { data, error } = await apiFetch(`/api/phrases/${id}`);
  if (error) {
    if (error.includes('404')) return null;
    throw new Error(error || 'Failed to fetch phrase');
  }
  return data;
};

/**
 * Fetch a phrase with all its category variations grouped
 */
export const fetchPhraseGrouped = async (id) => {
  const { data, error } = await apiFetch(`/api/phrases/grouped/${id}`);
  if (error) {
    if (error.includes('404')) return null;
    throw new Error(error || 'Failed to fetch grouped phrase');
  }
  return data;
};

/**
 * Get phrase statistics
 */
export const fetchPhraseStats = async () => {
  const { data, error } = await apiFetch('/api/phrases/stats/count');
  if (error) {
    throw new Error(error || 'Failed to fetch stats');
  }
  return data;
};

/**
 * Search phrases (convenience method)
 */
export const searchPhrasesApi = async (query) => {
  return fetchPhrases({ search: query });
};

/**
 * Get phrases by category (convenience method)
 */
export const getPhrasesByCategoryApi = async (categoryId) => {
  return fetchPhrases({ category: categoryId });
};
